import React, { useState, useEffect, useRef } from "react";
import "./tictactoeWeb.css";
import logo from "../../../assets/images/coinlogo.png";
import useUserInfo from "../../../Hooks/useUserInfo";
import cancelIcon from "../../../assets/Task/cancelicon.svg";
import { userGameRewards } from "../../../apis/user";
import { useAudio } from "../../../Providers/AudioProvider";
import task from "../../Task/Task";
import { logPageView } from "../../../components/analytics";
import { io } from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import { Base_Url } from "../../../apis/baseurl";
import Swal from "sweetalert2";

const TicTacToeWeb = () => {
  const { userDetails, updateUserInfo, watchScreen, updatewatchScreenInfo } =
    useUserInfo();
  const userId = userDetails?.userDetails?.telegramId;
  const [socket, setSocket] = useState(null);
  const [board, setBoard] = useState(Array(9).fill(null));
  const [playerSymbol, setPlayerSymbol] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [gameStatus, setGameStatus] = useState("initial"); // 'initial', 'waiting', 'playing', 'finished'
  const [winner, setWinner] = useState(null);
  const [gameFinished, setGameFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [winningCombination, setWinningCombination] = useState([]);
  const [isStrikeThrough, setIsStrikeThrough] = useState(false);
  const playClick = useAudio();
  const [copySuccess, setCopySuccess] = useState("");
  const [inputRoomId, setInputRoomId] = useState("");
  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);
  const waitingTimerRef = useRef(null);
  const [players, setPlayers] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [playAgainRequested, setPlayAgainRequested] = useState(false);
  const [iRequestedPlayAgain, setIRequestedPlayAgain] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showGameModeSelection, setShowGameModeSelection] = useState(true);
  const [showRoomLoading, setShowRoomLoading] = useState(false);
  const [apiCallMade, setApiCallMade] = useState(false); // Flag to track if API call has been made
  const [gameMode, setGameMode] = useState(null); // 'single', 'multiplayer'
  const [lastWinnerSocketId, setLastWinnerSocketId] = useState(null); // Store winner's socket ID for play again
  const [roomCreatorSocketId, setRoomCreatorSocketId] = useState(null); // Store room creator's socket ID
  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(Base_Url.base_url, {
      auth: {
        token: localStorage.getItem("token"), // Assuming you store the token in localStorage
      },
    });
    setSocket(newSocket);
    // Add connection status logs
    newSocket.on("connect", () => {
      console.log("Connected to server ");
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    return () => newSocket.disconnect();
  }, []);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    socket.on("gameState", (gameState) => {
      console.log("Received game state:");

      // Update local game state based on server data
      if (gameState.board) setBoard(gameState.board);
      if (gameState.players) {
        setPlayers(gameState.players);
        // Determine player symbol based on position in players array
        const playerIndex = gameState.players.indexOf(socket.id);
        setPlayerSymbol(playerIndex === 0 ? "X" : "O");
        
        // Track room creator (first player in the array)
        if (gameState.players.length > 0 && !roomCreatorSocketId) {
          setRoomCreatorSocketId(gameState.players[0]);
        }
      }
      if (gameState.currentPlayer) {
        setCurrentPlayer(gameState.currentPlayer);
        setIsMyTurn(gameState.currentPlayer === socket.id);
      }
      if (gameState.gameStatus) setGameStatus(gameState.gameStatus);
      if (gameState.roomId) setRoomId(gameState.roomId);

      // Handle game status changes
      if (gameState.gameStatus === "playing" && players.length === 2) {
        setIsMyTurn(gameState.currentPlayer === socket.id);
      }

      // Handle game over state
      if (gameState.gameStatus === "finished") {
        setGameFinished(true);
        
        // Store the winner's socket ID for play again functionality
        if (gameState.winner && gameState.winner !== "Draw" && gameState.winner !== "Disconnected") {
          setLastWinnerSocketId(gameState.winner);
        } else {
          setLastWinnerSocketId(null); // No winner for draws or disconnections
        }
        
        if (gameState.winner === "Draw" || gameState.winner === "draw") {
          // Handle draw scenario
          setWinner("Draw");
          const points = 0; // No points for draw
          setScore(points);
          // Only call handleApiResponse if this is the first time we're seeing this game state
          if (!gameFinished) {
            handleApiResponse(points.toString());
          }
        } else if (gameState.winner === "Disconnected") {
          // Handle disconnection scenario
          setWinner("Disconnected");
          const points = 0; // No points for disconnection
          setScore(points);
          // Only call handleApiResponse if this is the first time we're seeing this game state
          if (!gameFinished) {
            handleApiResponse(points.toString());
          }
        } else if (gameState.winner) {
          // Convert socket ID back to symbol for display
          const winnerPlayerIndex = gameState.players.indexOf(gameState.winner);
          const winnerSymbol = winnerPlayerIndex === 0 ? "X" : "O";
          setWinner(winnerSymbol);
          
          // Calculate score based on winner
          if (gameState.winner === socket.id) {
            // Current player won
            const points = 2000; // Fixed 2000 points for winning
            setScore(points);
            // Only call handleApiResponse if this is the first time we're seeing this game state
            if (!gameFinished) {
              handleApiResponse(points.toString());
            }
          } else {
            // Current player lost
            const points = 0; // No points for losing
            setScore(points);
            // Only call handleApiResponse if this is the first time we're seeing this game state
            if (!gameFinished) {
              handleApiResponse(points.toString());
            }
          }
        } else {
          // Fallback for no winner specified (other scenarios)
          setWinner("Draw");
          const points = 0; // No points
          setScore(points);
          // Only call handleApiResponse if this is the first time we're seeing this game state
          if (!gameFinished) {
            handleApiResponse(points.toString());
          }
        }
      }
    });

    socket.on("playerJoined", ({ players, roomId }) => {
      // Hide loading indicator if it was shown
      setShowRoomLoading(false);
      
      setPlayers(players);
      setRoomId(roomId);

      // Track room creator (first player in the room)
      if (players.length === 1) {
        setRoomCreatorSocketId(players[0]);
      }

      // If we have 2 players, start the game
      if (players.length === 2) {
        const initialGameState = {
          board: Array(9).fill(null),
          players: players,
          currentPlayer: players[0], // First player (room creator) starts
          gameStatus: "playing",
          roomId: roomId,
        };

        // Emit the initial game state to all players
        socket.emit("updateGameState", initialGameState);
      }
    });

    socket.on("playerLeft", () => {
      console.log("Other player disconnected");
      setWinner("Disconnected"); // This should be a special value to indicate disconnection
      setGameStatus("finished");
      setGameFinished(true);
      
      // Set points to 0 when opponent disconnects
      const points = 0;
      setScore(points);
      handleApiResponse(points.toString());
      
      // Send game over state to server
      const disconnectedGameState = {
        board: board,
        players: players,
        currentPlayer: currentPlayer,
        gameStatus: "finished",
        roomId: roomId,
        winner: "Disconnected", // Special value for disconnection
        points: {
          winner: 0,
          loser: 0,
          winnerSocketId: null,
          loserSocketId: null
        }
      };
      socket.emit("updateGameState", disconnectedGameState);
      
      // Remove the alert as it's redundant with the game over popup
      // alert("Other player disconnected");
    });

    socket.on("roomFull", () => {
      // Hide loading indicator if it was shown
      setShowRoomLoading(false);
      
      console.log("Room is full");
      setErrorMessage("Room is full. Please try another room.");
      setGameStatus("initial");
      setInputRoomId(""); // Clear the input field
    });

    socket.on("playAgainRequest", (data) => {
      console.log("Other player wants to play again");
      // Show that the other player wants to play again
      setPlayAgainRequested(true);
      setIRequestedPlayAgain(false);
    });

    socket.on("playAgainConfirmed", (gameState) => {
      console.log("Other player confirmed play again");
      // Update local game state for the new round
      if (gameState.board) setBoard(gameState.board);
      if (gameState.players) {
        setPlayers(gameState.players);
        // Determine player symbol based on position in players array
        const playerIndex = gameState.players.indexOf(socket.id);
        setPlayerSymbol(playerIndex === 0 ? "X" : "O");
      }
      if (gameState.currentPlayer) {
        setCurrentPlayer(gameState.currentPlayer);
        setIsMyTurn(gameState.currentPlayer === socket.id);
      }
      if (gameState.gameStatus) setGameStatus(gameState.gameStatus);

      // Reset game over state
      setWinner(null);
      setGameFinished(false);
      setWinningCombination([]);
      setIsStrikeThrough(false);
      setShowTimeoutPopup(false);
      setPlayAgainRequested(false);
      setIRequestedPlayAgain(false);
    });

    socket.on("roomExpired", (data) => {
      // Hide loading indicator if it was shown
      setShowRoomLoading(false);
      
      // Check if this is the current user's room (timeout) or someone trying to join an expired room
      if (roomId === data.roomId) {
        // This is the current user's room expiring due to timeout
        // Don't reset showTimeoutPopup here - let the timeout popup stay visible
        setRoomId(null);
        setGameStatus("initial");
        setWinner(null);
        setGameFinished(false);
        setWinningCombination([]);
        setIsStrikeThrough(false);
        setPlayers([]);
        setCurrentPlayer(null);
        setPlayAgainRequested(false);
        setIRequestedPlayAgain(false);
      } else {
        // Someone is trying to join an expired room
        setErrorMessage(
          data.message || "This room has expired and cannot be joined."
        );
        setGameStatus("initial");
        setInputRoomId(""); // Clear the input field
        // Clear error message after 5 seconds
        setTimeout(() => setErrorMessage(""), 5000);
      }
    });

    socket.on("roomDeleted", (data) => {
      // Room has been successfully deleted
    });

    socket.on("roomNotFound", (data) => {
      // Hide loading indicator if it was shown
      setShowRoomLoading(false);
      
      console.log("Room not found:", data);
      setErrorMessage("Room not found. Please check the Room ID and try again.");
      setGameStatus("initial");
      setInputRoomId(""); // Clear the input field
      // Clear error message after 5 seconds
      setTimeout(() => setErrorMessage(""), 5000);
    });

    socket.on("error", (error) => {
      // Hide loading indicator if it was shown
      setShowRoomLoading(false);
      
      console.log("Socket error:", error);
      setErrorMessage(error || "An error occurred. Please try again.");
      setGameStatus("initial");
      // Clear error message after 5 seconds
      setTimeout(() => setErrorMessage(""), 5000);
    });

    return () => {
      socket.off("gameState");
      socket.off("playerJoined");
      socket.off("playerLeft");
      socket.off("roomFull");
      socket.off("playAgainRequest");
      socket.off("playAgainConfirmed");
      socket.off("roomExpired");
      socket.off("roomDeleted");
      socket.off("roomNotFound");
      socket.off("error");
    };
  }, [socket, players]);

  // Setup waiting timer when room is created/joined
  useEffect(() => {
    // Clear any existing timer
    if (waitingTimerRef.current) {
      clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }

    // Set a new timer if we're in waiting state with a room ID
    if (gameStatus === "waiting" && roomId) {
      waitingTimerRef.current = setTimeout(() => {
        setShowTimeoutPopup(true);
        // Expire the room when timeout occurs
        if (socket) {
          socket.emit("expireRoom", { roomId: roomId });
        }
      }, 120000); // 120 seconds
    }

    // Clear timer when game starts or component unmounts
    return () => {
      if (waitingTimerRef.current) {
        clearTimeout(waitingTimerRef.current);
      }
    };
  }, [gameStatus, roomId]);

  // Clear timer when game starts
  useEffect(() => {
    if (gameStatus === "playing" && waitingTimerRef.current) {
      clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }
  }, [gameStatus]);

  const handleApiResponse = async (points) => {
    // Check if API call has already been made for this game session
    if (apiCallMade) {
      console.log('API call already made, skipping duplicate call');
      return;
    }
    
    // Set flag to prevent duplicate calls
    setApiCallMade(true);
    
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    
    try {
      const apiData = {
        gamePoints: points,
        gameType: "TicTacToeWeb",
      };
      console.log('Making API call with points:', points);
      const res = await userGameRewards(apiData);
      
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        setApiCallMade(false);
      }
    } catch (error) {
      console.error("API call failed:", error);
      // Reset flag if API call fails so we can try again
      setApiCallMade(false);
    }
  };

  const checkWinner = (currentBoard) => {
    const winningCombinations = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8],
      [0, 4, 8],
      [2, 4, 6],
    ];

    for (let combo of winningCombinations) {
      const [a, b, c] = combo;
      if (
        currentBoard[a] &&
        currentBoard[a] === currentBoard[b] &&
        currentBoard[a] === currentBoard[c]
      ) {
        setWinningCombination(combo); // Save the winning combination
        return currentBoard[a];
      }
    }
    if (!currentBoard.includes(null)) return "Draw"; // Handle draw
    return null;
  };

  const handleGameOver = (winningPlayer) => {
    setGameStatus("finished");
    setWinner(winningPlayer);
    setGameFinished(true);

    // Convert winning symbol to socket ID for backend (if not already done)
    let winnerSocketId = null;
    if (winningPlayer && winningPlayer !== "Draw" && winningPlayer !== "Disconnected") {
      const winnerPlayerIndex = winningPlayer === "X" ? 0 : 1;
      winnerSocketId = players[winnerPlayerIndex];
    } else if (winningPlayer === "Draw") {
      winnerSocketId = "Draw";
    } else if (winningPlayer === "Disconnected") {
      winnerSocketId = "Disconnected";
    }

    // Calculate points for final game state
    let winnerPoints = 0;
    let loserPoints = 0;
    if (winningPlayer === "Draw" || winningPlayer === "Disconnected") {
      winnerPoints = 0; // No points for draw or disconnection
      loserPoints = 0;
    } else if (winningPlayer === playerSymbol) {
      winnerPoints = 2000; // Fixed 2000 points for winning
      loserPoints = 0; // No points for losing
    } else if (winningPlayer) {
      winnerPoints = 0; // No points for losing
      loserPoints = 2000; // Fixed 2000 points for winning
    }

    // Send final game state to server
    const finalGameState = {
      board: board,
      players: players,
      currentPlayer: currentPlayer,
      gameStatus: "finished",
      roomId: roomId,
      winner: winnerSocketId,
      points: {
        winner: winnerPoints,
        loser: loserPoints,
        winnerSocketId: winnerSocketId,
        loserSocketId: winnerSocketId ? players.find(id => id !== winnerSocketId) : null
      }
    };
    socket.emit("updateGameState", finalGameState);

    if (winningPlayer === "Draw") {
      // Draw
      const points = 0; // No points for draw
      setScore(points);
      // handleApiResponse is now only called in the gameState event handler
    } else if (winningPlayer === playerSymbol) {
      const points = 2000; // Fixed 2000 points for winning
      setScore(points);
      // handleApiResponse is now only called in the gameState event handler
    } else if (winningPlayer) {
      const points = 0; // No points for losing
      setScore(points);
      // handleApiResponse is now only called in the gameState event handler
    }
  };

  const handleCellClick = (index) => {
    if (!isMyTurn || board[index] || gameStatus !== "playing") return;

    // Create a new board with the move
    const newBoard = [...board];
    newBoard[index] = playerSymbol;

    // Check for winner
    const winningSymbol = checkWinner(newBoard);

    // Determine next player
    const nextPlayer = players.find((id) => id !== currentPlayer);

    // Convert winning symbol to socket ID for backend
    let winnerSocketId = null;
    if (winningSymbol && winningSymbol !== "Draw") {
      // Find the socket ID of the player who has the winning symbol
      const winnerPlayerIndex = winningSymbol === "X" ? 0 : 1;
      winnerSocketId = players[winnerPlayerIndex];
    } else if (winningSymbol === "Draw") {
      winnerSocketId = "Draw";
    }

    // Calculate points for each player
    let winnerPoints = 0;
    let loserPoints = 0;
    if (winningSymbol && winningSymbol !== "Draw") {
      // Someone won
      winnerPoints = 2000; // Fixed 2000 points for winning
      loserPoints = 0; // No points for losing
    } else if (winningSymbol === "Draw") {
      // Draw
      winnerPoints = 0; // No points for draw
      loserPoints = 0; // No points for draw
    }

    // Create updated game state
    const updatedGameState = {
      board: newBoard,
      players: players,
      currentPlayer: nextPlayer,
      gameStatus:
        winningSymbol || !newBoard.includes(null) ? "finished" : "playing",
      roomId: roomId,
      winner: winnerSocketId, // Send socket ID instead of symbol
      points: {
        winner: winnerPoints,
        loser: loserPoints,
        winnerSocketId: winnerSocketId,
        loserSocketId: winnerSocketId ? players.find(id => id !== winnerSocketId) : null
      }
    };

    // Update local state
    setBoard(newBoard);
    setCurrentPlayer(nextPlayer);
    setIsMyTurn(false);

    if (winningSymbol || !newBoard.includes(null)) {
      handleGameOver(winningSymbol);
    }

    // Send updated game state to server
    socket.emit("updateGameState", updatedGameState);
  };

  const startOverNewGame = () => {
    // Reset all game states to initial values
    if (roomId && socket && gameMode === 'multiplayer') {
      // Notify other players that this player is leaving
      socket.emit('playerLeft', { roomId: roomId });
      // Don't emit expireRoom for manual exits - this prevents redirecting other players
    }
    setBoard(Array(9).fill(null));
    setGameStatus("initial"); // Set to 'initial' instead of 'waiting'
    setWinner(null);
    setGameFinished(false);
    setWinningCombination([]);
    setIsStrikeThrough(false);
    setRoomId(null); // Reset roomId to null to show initial screen
    setPlayerSymbol(null); // Reset player symbol
    setInputRoomId("");
    setShowTimeoutPopup(false);
    setPlayers([]);
    setCurrentPlayer(null);
    setPlayAgainRequested(false);
    setIRequestedPlayAgain(false);
    setErrorMessage(""); // Clear any error messages
    setShowGameModeSelection(true); // Show game mode selection screen
    setApiCallMade(false); // Reset API call flag for new game
    setGameMode(null); // Reset game mode
    setLastWinnerSocketId(null); // Reset winner for new game
    setRoomCreatorSocketId(null); // Reset room creator for new game
  };
  const playAgainInSameSession = () => {
    // Set flag to show we want to play again
    setPlayAgainRequested(true);
    setIRequestedPlayAgain(true);
    // Notify other player that we want to play again
    socket.emit("playAgainRequest", { roomId: roomId });
  };

  const confirmPlayAgain = () => {
    // Reset game state but keep the same room and players
    setBoard(Array(9).fill(null));
    setGameStatus("playing");
    setWinner(null);
    setGameFinished(false);
    setWinningCombination([]);
    setIsStrikeThrough(false);
    setShowTimeoutPopup(false);
    setPlayAgainRequested(false);
    setIRequestedPlayAgain(false);
    setApiCallMade(false); // Reset API call flag for new game

    // Switch player symbols for the next round (X becomes O, O becomes X)
    setPlayerSymbol(playerSymbol === "X" ? "O" : "X");

    // Determine who gets the first turn based on the rules:
    // 1. If there was a winner, winner gets first turn
    // 2. If it was a draw, room creator gets first turn
    let firstPlayer;
    if (lastWinnerSocketId) {
      // There was a winner, winner gets first turn
      firstPlayer = lastWinnerSocketId;
      console.log('Winner gets first turn:', firstPlayer);
    } else {
      // It was a draw, room creator gets first turn
      firstPlayer = roomCreatorSocketId || players[0];
      console.log('Draw - room creator gets first turn:', firstPlayer);
    }

    setCurrentPlayer(firstPlayer);
    setIsMyTurn(firstPlayer === socket.id);

    // Create new game state for the next round
    const newGameState = {
      board: Array(9).fill(null),
      players: players,
      currentPlayer: firstPlayer, // Winner or room creator gets first turn
      gameStatus: "playing",
      roomId: roomId,
      winner: null,
    };

    // Send updated game state to server and notify other player
    socket.emit("updateGameState", newGameState);
    socket.emit("playAgainConfirmed", newGameState);
  };

  const startNewGame = () => {
    const newRoomId = uuidv4();
    // Reset all game states to initial values
    setBoard(Array(9).fill(null));
    setGameStatus("waiting");
    setWinner(null);
    setGameFinished(false);
    setWinningCombination([]);
    setIsStrikeThrough(false);
    setRoomId(newRoomId);
    setErrorMessage(""); // Clear any error messages
    setLastWinnerSocketId(null); // Reset winner for new game
    setRoomCreatorSocketId(null); // Reset room creator for new game
    socket.emit("joinRoom", { roomId: newRoomId, gameType: "tictactoe" });
  };

  const joinGame = (roomIdToJoin) => {
    if (!roomIdToJoin) {
      setErrorMessage("Please enter a Room ID");
      return;
    }
    
    // Basic validation for room ID format (should be a valid UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(roomIdToJoin)) {
      setErrorMessage("Invalid Room ID format. Please check the ID and try again.");
      return;
    }
    
    setBoard(Array(9).fill(null));
    setGameStatus("waiting");
    setWinner(null);
    setGameFinished(false);
    setWinningCombination([]);
    setIsStrikeThrough(false);
    setErrorMessage(""); // Clear any error messages
    setLastWinnerSocketId(null); // Reset winner for new game
    setRoomCreatorSocketId(null); // Reset room creator for new game
    
    // Show loading state while checking room
    setShowRoomLoading(true);
    
    socket.emit("joinRoom", { roomId: roomIdToJoin, gameType: "tictactoe" });
  };

  const toogleMenu = () => {
    playClick();
    setErrorMessage(""); // Clear any error messages
    
    // Show confirmation dialog if user is in a game
    if (gameStatus === "playing" || gameStatus === "waiting" || roomId) {
      Swal.fire({
        title: 'Exit Game?',
        text: 'Are you sure you want to exit the game? This will end the current session.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ff4444',
        cancelButtonColor: '#4a90e2',
        confirmButtonText: 'Yes, Exit',
        cancelButtonText: 'Cancel',
        background: 'rgba(0, 0, 0, 0.9)',
        color: '#fff',
        customClass: {
          popup: 'swal2-popup-dark',
          title: 'swal2-title-dark',
          content: 'swal2-content-dark'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          // User confirmed exit, proceed with the original functionality
          if (roomId && socket && gameMode === 'multiplayer') {
            // Notify other players that this player is leaving
            socket.emit('playerLeft', { roomId: roomId });
            // Don't emit expireRoom for manual exits - this prevents redirecting other players
          }
          if (roomId) {
            setRoomId(null);
            setGameStatus("initial");
          } else if (!showGameModeSelection) {
            setShowGameModeSelection(true);
            setGameStatus("initial");
          } else {
            updateUserInfo((prev) => ({
              ...prev,
              isPlay: false,
              currentComponent: task,
              currentComponentText: "task",
              lastComponent: userDetails?.userDetails.currentComponent,
              lastComponentText: userDetails?.userDetails.currentComponentText,
              isMenu: true,
              menuCount: userDetails?.userDetails?.menuCount + 1,
            }));
          }
        }
        // If user cancels, do nothing (stay in the game)
      });
    } else {
      // No confirmation needed if not in a game, proceed directly
      if (roomId) {
        setRoomId(null);
        setGameStatus("initial");
      } else if (!showGameModeSelection) {
        setShowGameModeSelection(true);
        setGameStatus("initial");
      } else {
        updateUserInfo((prev) => ({
          ...prev,
          isPlay: false,
          currentComponent: task,
          currentComponentText: "task",
          lastComponent: userDetails?.userDetails.currentComponent,
          lastComponentText: userDetails?.userDetails.currentComponentText,
          isMenu: true,
          menuCount: userDetails?.userDetails?.menuCount + 1,
        }));
      }
    }
  };

  const getStrikeClass = () => {
    if (!isStrikeThrough || winningCombination.length === 0) return "";
    const [a, b, c] = winningCombination;

    if (a === 0 && b === 1 && c === 2) return "strike-horizontal-top";
    if (a === 3 && b === 4 && c === 5) return "strike-horizontal-middle";
    if (a === 6 && b === 7 && c === 8) return "strike-horizontal-bottom";
    if (a === 0 && b === 3 && c === 6) return "strike-vertical-left";
    if (a === 1 && b === 4 && c === 7) return "strike-vertical-middle";
    if (a === 2 && b === 5 && c === 8) return "strike-vertical-right";
    if (a === 0 && b === 4 && c === 8) return "strike-diagonal-left";
    if (a === 2 && b === 4 && c === 6) return "strike-diagonal-right";

    return "";
  };
  const copyToClipboard = async (text) => {
    try {
      // If Clipboard API is available and not sandboxed
      if (navigator.clipboard && window.isSecureContext && window.top === window.self) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for Telegram or iframe contexts
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.width = "1px";
        textArea.style.height = "1px";
        textArea.style.padding = "0";
        textArea.style.border = "none";
        textArea.style.outline = "none";
        textArea.style.boxShadow = "none";
        textArea.style.background = "transparent";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
  
      setCopySuccess("Copied!");
      setTimeout(() => setCopySuccess(""), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
      setCopySuccess("Failed to copy");
    }
  };
  
  

  const navigateToSinglePlayer = () => {
    playClick();
    // Reset timeout popup state
    setShowTimeoutPopup(false);
    setErrorMessage(""); // Clear any error messages
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: true,
      currentComponent: require("../../Task/tictactoe/tictactoe").default,
      currentComponentText: "tictactoe",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
    }));
  };

  const selectMultiplayerMode = () => {
    playClick();
    setShowGameModeSelection(false);
    setGameMode('multiplayer');
  };

  const selectSinglePlayerMode = () => {
    playClick();
    navigateToSinglePlayer();
  };

  const handleExitWaitingRoom = () => {
    Swal.fire({
      title: 'Exit Waiting Room?',
      text: 'Are you sure you want to leave? This will close the room and any waiting players will be disconnected.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff4444',
      cancelButtonColor: '#4a90e2',
      confirmButtonText: 'Yes, Exit',
      cancelButtonText: 'Cancel',
      background: 'rgba(0, 0, 0, 0.9)',
      color: '#fff',
      customClass: {
        popup: 'swal2-popup-dark',
        title: 'swal2-title-dark',
        content: 'swal2-content-dark'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // Execute exit functionality
        if (roomId && socket && gameMode === 'multiplayer') {
          // Notify other players that this player is leaving
          socket.emit('playerLeft', { roomId: roomId });
          // Don't emit expireRoom for manual exits - this prevents redirecting other players
        }
        setRoomId(null);
        setGameStatus("initial");
        setShowGameModeSelection(true);
      }
    });
  };

  return (
    <div
      className="multiplayer-tic-container"
      style={{ pointerEvents: "fill" }}
    >
      {(!roomId || gameStatus !== "waiting") && (
        <img
          onClick={toogleMenu}
          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
          className="cancel-fullmario"
          style={{ cursor: "pointer" }}
        />
      )}
      <div className="col-12">
        <h2 className="welcometextmeme">TIC TAC TOE</h2>
        <p className="multiplayer-tic-subtitle">
          {showGameModeSelection ? "CHOOSE YOUR GAME MODE" : "MULTIPLAYER MODE - PLAY WITH FRIENDS!"}
        </p>
      </div>

      {errorMessage && (
        <div className="multiplayer-tic-error">
          <p>{errorMessage}</p>
        </div>
      )}

      {showGameModeSelection && (
        <div className="multiplayer-tic-game-mode-selection">
          <button 
            className="multiplayer-tic-playgame" 
            onClick={selectSinglePlayerMode}
          >
            🤖 PLAY WITH THE THEMEMETV
            <div className="button-description">Challenge TheMemeTV</div>
          </button>
          <button 
            className="multiplayer-tic-playgame" 
            onClick={selectMultiplayerMode}
          >
            👥 MULTIPLAYER
            <div className="button-description">Play with friends online</div>
          </button>
        </div>
      )}

      {!showGameModeSelection && (gameStatus === "waiting" || gameStatus === "initial") && !roomId && (
        <div className="multiplayer-tic-options">
          <button className="multiplayer-tic-playgame" onClick={startNewGame}>
            CREATE NEW GAME
          </button>
          <div className="multiplayer-tic-join-game">
            <input
              type="text"
              placeholder="Enter Room ID"
              value={inputRoomId}
              onChange={(e) => setInputRoomId(e.target.value)}
              disabled={showRoomLoading}
            />
            <button
              className="multiplayer-tic-playgame"
              onClick={() => joinGame(inputRoomId)}
              disabled={showRoomLoading}
            >
              {showRoomLoading ? "CHECKING ROOM..." : "JOIN GAME"}
            </button>
          </div>
        </div>
      )}

      {roomId && gameStatus === "waiting" && (
        <div className="multiplayer-tic-waiting-room">
          <div className="waiting-room-header">
            <h3>Waiting Room</h3>
            <button 
              className="waiting-room-exit-button"
              onClick={handleExitWaitingRoom}
            >
              ✕
            </button>
          </div>
          <div className="waiting-status">
            <h4 style={{ color: '#4a90e2', margin: '0 0 10px 0', fontSize: '18px' }}>Room ID:</h4>
            <div className="multiplayer-tic-room-id-container">
              <input
                type="text"
                value={roomId}
                readOnly
                className="multiplayer-tic-room-id-display"
              />
              <button
                className="multiplayer-tic-copy-button"
                onClick={() => copyToClipboard(roomId)}
              >
                {copySuccess || "Copy"}
              </button>
            </div>
            <p className="multiplayer-tic-subtitle">Share this Room ID with your friend to play together</p>
            <p className="multiplayer-tic-subtitle">Wait till the opponent joins the game</p>
          </div>
        </div>
      )}

      {gameStatus === "playing" && (
        <>
          <div className="multiplayer-tic-game-status">
            <p>{isMyTurn ? "Your turn" : "Opponent's turn"}</p>
            <p>You are playing as: {playerSymbol}</p>
          </div>
          <div className={`multiplayer-tic-board ${getStrikeClass()}`}>
            {board.map((cell, index) => (
              <div
                key={index}
                onClick={() => handleCellClick(index)}
                className={`multiplayer-tic-cell ${cell ? cell : ""} ${
                  cell ? "filled" : ""
                }`}
              >
                {cell}
              </div>
            ))}
          </div>
        </>
      )}

      {gameFinished && (
        <div className="multiplayer-tic-overlay">
          <div className="multiplayer-tic-popup">
            <div className="multiplayer-tic-popup-content">
              <h2 className="multiplayer-tic-epic">Game Over!</h2>
              <div className="row text-center">
                <div className="col-12">
                  <div
                    className="multiplayer-tic-epic-div"
                    style={{ padding: "30px 40px" }}
                  >
                    <p className="multiplayer-tic-points-label">
                      TOKENS EARNED
                    </p>
                    <div className="multiplayer-tic-points-value">
                      <img
                        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                        alt="logo"
                      />
                      <p className="multiplayer-tic-points-number">{score}</p>
                    </div>
                  </div>
                  <p className="multiplayer-tic-winner-text">
                    {winner === "Draw"
                      ? "It's a draw!"
                      : winner === playerSymbol
                      ? "You won!"
                      : winner === "Disconnected"
                      ? "Opponent Disconnected"
                      : "Opponent won!"}
                  </p>
                </div>
              </div>
            </div>
            <div className="multiplayer-tic-playagain">
              {!playAgainRequested ? (
                <>
                  {winner !== "Disconnected" && (
                    <button
                      className="multiplayer-tic-playgame"
                      onClick={playAgainInSameSession}
                    >
                      PLAY AGAIN
                    </button>
                  )}
                  <button
                    className="multiplayer-tic-playgame"
                    onClick={startOverNewGame}
                  >
                    {"RETURN TO MENU"}
                  </button>
                </>
              ) : iRequestedPlayAgain ? (
                <>
                  <p className="multiplayer-tic-waiting-text">
                    Waiting for other player to respond...
                  </p>
                  <button
                    className="multiplayer-tic-playgame"
                    onClick={startOverNewGame}
                  >
                    RETURN TO MENU
                  </button>
                </>
              ) : (
                <>
                  <p className="multiplayer-tic-waiting-text">
                    Other player wants to play again!
                  </p>
                  <button
                    className="multiplayer-tic-playgame"
                    onClick={confirmPlayAgain}
                  >
                    CONFIRM PLAY AGAIN
                  </button>
                  <button
                    className="multiplayer-tic-playgame"
                    onClick={startOverNewGame}
                  >
                   RETURN TO MENU
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showTimeoutPopup && (
        <div className="multiplayer-tic-overlay">
          <div className="multiplayer-tic-popup">
            <div className="multiplayer-tic-popup-content">
              <h2 className="multiplayer-tic-epic">No Player Joined</h2>
              <p>It seems no one has joined your game within the time limit.</p>
              <p>What would you like to do?</p>
            </div>
            <div className="multiplayer-tic-playagain">
              <button
                className="multiplayer-tic-playgame"
                onClick={startOverNewGame}
              >
                RETURN TO MENU
              </button>
              <button
                className="multiplayer-tic-playgame"
                onClick={navigateToSinglePlayer}
              >
                PLAY WITH THEMEMETV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicTacToeWeb;
